import * as RadixTooltip from '@radix-ui/react-tooltip';
import { useMediaQuery } from '@uidotdev/usehooks';
import type { ReactElement } from 'react';
import { isMacPlatform } from '../helpers/platform';
import { formatShortcut } from '../helpers/use-keyboard-shortcut';

type HintTooltipProps = {
  text: string;
  shortcut?: string;
  children: ReactElement;
};

/**
 * A hint affordance: a slower-to-open (700ms), arrow-less tooltip that pairs a
 * short label with an optional keyboard-shortcut chip. Uses the Radix tooltip
 * primitives directly rather than the shared `Tooltip` wrapper because it needs
 * no arrow and an unconstrained width — relies on the app-root `TooltipProvider`.
 * Radix closes the tooltip on trigger activation, so clicking the control
 * dismisses the hint without any manual open-state bookkeeping.
 */
export function HintTooltip({ text, shortcut, children }: HintTooltipProps) {
  const isTouch = useMediaQuery('(pointer: coarse)');

  if (isTouch) {
    return children;
  }

  const label = shortcut ? formatShortcut(shortcut, isMacPlatform()) : null;

  return (
    <RadixTooltip.Root delayDuration={700}>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side="bottom"
          sideOffset={4}
          collisionPadding={8}
          className="z-tooltip rounded-md bg-tooltip px-2 py-1.5 text-tooltip-foreground shadow-soft"
        >
          <span className="inline-flex items-center gap-2 text-xs whitespace-nowrap">
            <span>{text}</span>
            {label && (
              <kbd className="rounded-sm bg-white/15 px-1.5 py-0.5 font-sans text-xs leading-none">
                {label}
              </kbd>
            )}
          </span>
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}
